import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { listPublishAccounts, listTemplates } from '@/api/playPublish';

// App Factory's "publish it ourselves": once the AAB is built the run starts a
// First publication job on this Play account with this template's answers.
// On a shared console (somebody else's developer account that invited one of
// our browser profiles) the run also makes the Firebase project in that same
// profile, and the job waits until the owner has invited our service account.
// "The publisher's own console" makes such an account on the fly: the run takes
// an empty Gmail, sends it with our service account to the publisher's chat,
// and publishes once their "Access granted" passes our check.

export interface PublishOnValue { accountId: string; templateId: string }
export const NO_PUBLISH: PublishOnValue = { accountId: '', templateId: '' };
/** The account choice for "the publisher's own console, via our Gmail". */
export const PUBLISHER_CONSOLE = 'publisher';

/** The fields createFactoryRun takes, or nothing when the run only hands off. */
export function publishOnInput(v: PublishOnValue | null) {
  if (!v?.accountId) return {};
  const template = Number(v.templateId) || null;
  return v.accountId === PUBLISHER_CONSOLE
    ? { console_access: true, publish_template_id: template }
    : { play_account_id: Number(v.accountId), publish_template_id: template };
}

export function FactoryPublishOn({ value, onChange, className = '' }: {
  value: PublishOnValue | null;
  /** null switches it off. */
  onChange: (v: PublishOnValue | null) => void;
  className?: string;
}) {
  const on = value !== null;
  const accounts = useQuery({ queryKey: ['publish-accounts'], queryFn: listPublishAccounts, enabled: on });
  const templates = useQuery({ queryKey: ['publish-templates'], queryFn: listTemplates, enabled: on });
  const account = (accounts.data ?? []).find((a) => String(a.id) === value?.accountId);

  return (
    <div className={className}>
      <label className="flex items-start gap-2 text-xs text-content-subtle">
        <input type="checkbox" className="mt-0.5" checked={on} onChange={(e) => onChange(e.target.checked ? NO_PUBLISH : null)} />
        <span>Publish it ourselves through First publication once the AAB is built (the handoff link is still issued, for the books)</span>
      </label>
      {on && (
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div>
            <label className="block text-xs font-semibold text-content-muted mb-1">Play account</label>
            <Select
              value={value.accountId}
              onChange={(id) => onChange({ ...value, accountId: id })}
              searchable
              placeholder={accounts.isLoading ? 'Loading…' : 'Where to publish'}
              options={[
                { value: PUBLISHER_CONSOLE, label: 'The publisher’s own console — our Gmail goes to their chat' },
                ...(accounts.data ?? []).filter((a) => a.enabled).map((a) => ({
                  value: String(a.id),
                  label: a.browser_profile_id ? `${a.name} · shared via ${a.browser_profile_name}` : a.name,
                })),
              ]}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-content-muted mb-1">Template</label>
            <Select
              value={value.templateId}
              onChange={(id) => onChange({ ...value, templateId: id })}
              placeholder={templates.isLoading ? 'Loading…' : 'Questionnaire answers'}
              options={(templates.data ?? []).map((t) => ({ value: String(t.id), label: `${t.name} · ${t.track}` }))}
            />
          </div>
          {value.accountId === PUBLISHER_CONSOLE && (
            <p className="sm:col-span-2 text-xs text-content-subtle">
              The run takes an empty Gmail from Browser Profiles (scheme Firebase, no projects), makes the app’s Firebase project
              and our service account in it, then sends both addresses with the permissions to tick and an “Access granted” button
              to the publisher’s chat. Their press makes the runner accept the invite and check that the console lets us create
              apps; publishing starts once it passes. Permissions the console asks for later go to the same chat.
            </p>
          )}
          {account?.browser_profile_id && (
            <p className="sm:col-span-2 text-xs text-content-subtle">
              A shared console: the Firebase project is made in profile “{account.browser_profile_name}” (raise its limit in
              Browser Profiles if it is full), and publishing waits until the owner has invited{' '}
              {account.sa_client_email ? <span className="font-mono">{account.sa_client_email}</span> : 'our service account (set it up in First publication first)'}
              {account.sa_invited_at ? ' — already done.' : '.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
